import { trpc as IssueStatistics } from '@tencent/tpro_rmonitor_web_server_issue_statistics';
import { Empty, SelectProps, TabPaneProps } from 'antd';
import ReactECharts, { EChartsOption } from 'echarts-for-react';
import { LineChart } from 'echarts/charts';
import {
  GridComponent,
  TooltipComponent,
  LegendComponent,
} from 'echarts/components';
import * as echarts from 'echarts/core';
import { CanvasRenderer } from 'echarts/renderers';
import { isEmpty } from 'lodash-es';
import moment from 'moment';
import React, { useEffect } from 'react';
import { DEFUALT_SPLIT_NUMBER, LINER_ECHART_COLORS } from '@/infrastructure/echart-config';
import { formatNum } from '@/infrastructure/utils/digit';
import { DATE_TIME_FORMAT, formatTimestamp, HH_MM_SS } from '@/infrastructure/utils/timestamp';
import { IssueTimeType, IssueMetricType as IssueType } from '@/proto/issue';
import { TimeType } from '@/proto/metric';
import { ITrend, StatData } from '@/proto/statistics';

import { IState } from '..';

type MetricGroupItem = {
  stat: StatData;
  name: string;
};

type CombineDisplayItem = {
  name: TabPaneProps['tab'];
  tabKey: any;
  unitConfig?: { // 单位配置 不传则无单位切换
    defaultUnit: SelectProps['value']; // 默认值
    options: SelectProps['options']; // 单位选项
    unitFormat: (value: SelectProps['value']) => (value: (undefined|number|null)) => (undefined|number|null); // 单位格式化方法
  },
  type?: string
};

type IssueMetricType = IssueStatistics.rmonitor.web_server.IssueMetricType;
interface Iprops {
  loading: boolean;
  chartData: ITrend[];
  compareChartData?: ITrend[];
  compareTimeWindow?: number;
  unitLabel: string;
  timeWindow?: number;
  tabKey: IssueMetricType;
  tabName: string;
  unitFormat?: (value: (undefined|number|null)) => (undefined|number|null);
  groupData: Record<string, ITrend[]>;
  metricStateGroup?: MetricGroupItem[];
  combineDisplayGroup?: CombineDisplayItem[];
  state: IState
}


export const Chart: React.FC<Iprops> = (props: Iprops) => {
  const { loading,
    timeWindow,
    chartData,
    tabKey,
    tabName,
    unitLabel,
    unitFormat,
    groupData,
    compareChartData,
    compareTimeWindow,
    state,
  } = props;
  let chartRef: any =  null;
  echarts.use([CanvasRenderer, LegendComponent, TooltipComponent, GridComponent, LineChart]);
  const KEY_NEED_PERCENT = [
    IssueType.ISSUE_METRIC_HTTP_ERR_RATE,
  ];
  useEffect(() => {
    let options;
    if (props.metricStateGroup) {
      options = getGroupChartOptions();
    } else if (props.combineDisplayGroup) {
      options = getCombineChartOptions();
    } else {
      options = getChartOptions();
    }

    chartRef?.setOption({ ...options, series: {} }, true);
    chartRef?.setOption(options, true);
  }, [timeWindow, chartData, tabKey, unitLabel, groupData, state]);

  const getSeries = (chartData: ITrend[], unitFormatArg?: any): number[] => {
    const unitFormatConfig =   unitFormatArg || unitFormat;
    const defaultZero = 0;
    return chartData.map((item) => {
      if (item.value) {
        return (unitFormatConfig ? unitFormatConfig(item?.value) : parseInt(String(item?.value), 10)) || defaultZero;
      }
      return defaultZero;
    }) || [];
  };

  const formatGroupData = () => {
    const series: any = [];
    let newXAxisData: string[] = [];

    props.metricStateGroup?.forEach((opt) => {
      const chartData = groupData[opt.stat] || [];
      const xAxisData = chartData.filter(item => item.time < moment().unix())
        .map(item => formatTimestamp(item.time, DATE_TIME_FORMAT));
      const compareXAxisData = compareChartData?.filter?.(item => item.time < moment().unix())
        .map(item => formatTimestamp(item.time, DATE_TIME_FORMAT)) || [];
      // 获取legend 图例
      newXAxisData = xAxisData;
      let newCompareXAxisData = compareXAxisData;

      const seriesData: number[] = getSeries(chartData).slice(0, xAxisData.length);
      const compareSeriesData: number[] = getSeries(compareChartData || []).slice(0, compareXAxisData.length);


      // 相同聚合 但日期不相同则补充数据
      if (compareChartData
        && timeWindow === compareTimeWindow && xAxisData?.length && compareXAxisData?.length
        && (xAxisData?.[0] !== compareXAxisData?.[0]
          || xAxisData?.[xAxisData?.length - 1] !== compareXAxisData?.[compareXAxisData?.length - 1])
      ) {
        // 时间范围小于等于1天，则使用时间范围最大的作为X轴
        if ((timeWindow === IssueTimeType.TIME_TYPE_MINUTE5 || timeWindow === IssueTimeType.TIME_TYPE_HOUR)
          && ((moment(xAxisData?.[xAxisData?.length - 1]).diff(moment(xAxisData?.[0]), 'day') < 1) && (moment(compareXAxisData?.[compareXAxisData?.length - 1]).diff(moment(compareXAxisData?.[0]), 'day') < 1))
        ) {
          const differenceMax = Math.max(newXAxisData?.length, newCompareXAxisData?.length);
          const differenceMin = Math.min(newXAxisData?.length, newCompareXAxisData?.length);
          const difference = differenceMax - differenceMin;
          if (newXAxisData?.length === differenceMin) {
            const diffList = Array.from(
              Array(difference),
              (_i, index) => moment(newXAxisData?.[newXAxisData?.length - 1]).add(index + 1, 'days')
                .format(DATE_TIME_FORMAT),
            );
            newXAxisData.push(...diffList);
          }
          if (newCompareXAxisData?.length === differenceMin) {
            const compareDiffList = Array.from(
              Array(difference),
              (_i, index) => moment(newCompareXAxisData?.[newCompareXAxisData?.length - 1]).add(index + 1, 'days')
                .format(DATE_TIME_FORMAT),
            );
            newCompareXAxisData.push(...compareDiffList);
          }
          const seriesDataUnshiftIndex = newXAxisData.findIndex(value => moment(value).format(HH_MM_SS)
            === moment(xAxisData[0]).format(HH_MM_SS));
          const seriesDataPushIndex = (newXAxisData.length
            - newXAxisData.findIndex(value => moment(value).format(HH_MM_SS)
              === moment(xAxisData[xAxisData.length - 1]).format(HH_MM_SS))) - 1;

          seriesData.unshift(...Array(seriesDataUnshiftIndex >= 0 ? seriesDataUnshiftIndex : 0).fill(undefined));
          seriesData.push(...Array(seriesDataPushIndex >= 0 ? seriesDataPushIndex : 0).fill(undefined));
          const compareSeriesDataUnshiftIndex = newCompareXAxisData.findIndex(value => moment(value).format(HH_MM_SS)
            === moment(compareXAxisData[0]).format(HH_MM_SS));
          const compareSeriesDataPushIndex = (newCompareXAxisData.length
            - compareXAxisData.findIndex(value => moment(value).format(HH_MM_SS)
              === moment(compareXAxisData[compareXAxisData.length - 1]).format(HH_MM_SS)) - 1);
          compareSeriesData.unshift(...Array(compareSeriesDataUnshiftIndex >= 0
            ? compareSeriesDataUnshiftIndex : 0).fill(undefined));
          compareSeriesData.push(...Array(compareSeriesDataPushIndex >= 0
            ? compareSeriesDataPushIndex : 0).fill(undefined));
        } else {
          const differenceMax = Math.max(newXAxisData?.length, newCompareXAxisData?.length);
          const differenceMin = Math.min(newXAxisData?.length, newCompareXAxisData?.length);
          const difference = differenceMax - differenceMin;
          if (newXAxisData?.length === differenceMin) {
            const diffList = Array.from(
              Array(difference),
              (_i, index) => moment(newXAxisData?.[newXAxisData?.length - 1]).add(index + 1, 'days')
                .format(DATE_TIME_FORMAT),
            );
            newXAxisData.push(...diffList);
          }
          if (newCompareXAxisData?.length === differenceMin) {
            const compareDiffList = Array.from(
              Array(difference),
              (_i, index) => moment(newCompareXAxisData?.[newCompareXAxisData?.length - 1]).add(index + 1, 'days')
                .format(DATE_TIME_FORMAT),
            );
            newCompareXAxisData.push(...compareDiffList);
          }


          const seriesDataUnshiftIndex = newXAxisData.findIndex(value => value === xAxisData[0]);
          const seriesDataPushIndex = (newXAxisData.length
            - newXAxisData.findIndex(value => value === xAxisData[xAxisData.length - 1])) - 1;

          seriesData.unshift(...Array(seriesDataUnshiftIndex >= 0 ? seriesDataUnshiftIndex : 0).fill(undefined));
          seriesData.push(...Array(seriesDataPushIndex >= 0 ? seriesDataPushIndex : 0).fill(undefined));
          const compareSeriesDataUnshiftIndex = newCompareXAxisData.findIndex(value => value
            === compareXAxisData[0]);
          const compareSeriesDataPushIndex = (newCompareXAxisData.length
            - compareXAxisData.findIndex(value => value
              === compareXAxisData[compareXAxisData.length - 1]) - 1);
          compareSeriesData.unshift(...Array(compareSeriesDataUnshiftIndex >= 0
            ? compareSeriesDataUnshiftIndex : 0).fill(undefined));
          compareSeriesData.push(...Array(compareSeriesDataPushIndex >= 0
            ? compareSeriesDataPushIndex : 0).fill(undefined));
        }
      }

      const curSeries = {
        id: opt.stat,
        xAxisIndex: 0,
        name: opt.name,
        smooth: timeWindow !== IssueTimeType.TIME_TYPE_MINUTE5,
        data: seriesData,
        type: 'line',
        symbolSize: 7,
        showAllSymbol: false,
        itemStyle: {
          borderWidth: 16,
          borderType: 'solid',
        },
        markPoint: {
          id: 'rect',
          animation: false,
          symbol: 'rect',
          symbolSize: [16, 2560],
          symbolOffset: [0, '-50%'],
          label: {
            show: false,
          },
          itemStyle: {
            color: 'rgba(0, 0, 0, 0)',
          },
          data: [],
        },
        endLabel: {
          position: 'top',
          distance: -18,
          offset: [0, -10],
          show: true,
          formatter: (params: any) => {
            const date = moment(params.name);
            const momentDate = moment();
            if (timeWindow === TimeType.WEEK) {
              const weekDate = moment(params.name).add(6, 'days');
              if (params.dataIndex === seriesData.length - 1 && weekDate.isSameOrAfter(momentDate, 'day')) {
                return '统计中';
              }
            }
            if (timeWindow === TimeType.MONTH) {
              if (params.dataIndex === seriesData.length - 1 && date.isSameOrAfter(momentDate, 'month')) {
                return '统计中';
              }
            }
            if (params.dataIndex === seriesData.length - 1 && momentDate.isSame(date, 'day')) {
              if (timeWindow === TimeType.DAY) {
                return '统计中';
              }
              if (timeWindow === TimeType.HOUR && momentDate.isSame(date, 'hour')) {
                return '统计中';
              }
              if (timeWindow === TimeType.MINUTE5) {
                let minutes: string | string[] = momentDate.format('mm').split('');
                minutes = `${minutes[0]}${Number(minutes?.[1]) >= 5 ? '5' : '0'}`;
                const startsTime = moment(momentDate.format(`YYYY-MM-DD HH:${minutes}:00`));
                if (date.isSame(startsTime)) {
                  return '统计中';
                }
                return '';
              }
            }

            return '';
          },
        },
      };

      series.push(curSeries);
    });

    const xAxis = [{
      type: 'category',
      axisTick: false,
      splitLine: {
        show: false,
      },
      axisLabel: {
        interval: 'auto',
        textStyle: {
          color: '#7B889C',
        },
        formatter(value: moment.MomentInput) {
          switch (timeWindow) {
            case IssueTimeType.TIME_TYPE_DAY:
              return moment(value).format('MM-DD');
            case IssueTimeType.TIME_TYPE_HOUR:
              return moment(value).format('HH');
            case TimeType.WEEK:
              return `${moment(value).format('MM-DD')}~${moment(value).add(6, 'days')
                .format('MM-DD')}`;
            case TimeType.MONTH:
              return moment(value).format('YY-MM');
            default:
              return moment(value).format('HH:mm');
          }
        },
      },
      data: newXAxisData,
      axisLine: {
        lineStyle: {
          color: '#DCE3EE',
        },
      },
    },
    ];

    const ySuffix = unitLabel;

    return {
      xAxis,
      ySuffix,
      series,
    };
  };

  const getGroupChartOptions = (): EChartsOption => {
    const data = formatGroupData();

    // 从metricStateGroup动态生成series
    const legendData = props.metricStateGroup?.map(item => item.name) || [];

    return  {
      color: LINER_ECHART_COLORS,
      animationDuration: 100,
      legend: {
        show: true, // 总是显示全部数据
        bottom: 0,
        data: legendData,
      },
      grid: {
        left: 10,
        top: 20,
        bottom: 40,
        right: 30,
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: {
          show: true,
          type: 'line',
          lineStyle: {
            width: 16,
            type: 'solid',
            color: 'rgba(194, 218, 255, 0.5)',
            shadowStyle: {
              color: 'rgba(194, 218, 255, 0.5)',
            },
          },
        },
        formatter(paramsList: any) {
          let str = '';
          const staticParam = paramsList?.[0];
          if (staticParam?.data !== undefined) {
            const date = staticParam?.name;
            const isTimeTypeHour = IssueTimeType.TIME_TYPE_HOUR === timeWindow;
            const momentDate = moment(date, DATE_TIME_FORMAT);
            if (timeWindow === IssueTimeType.TIME_TYPE_WEEK) {
              str += `${momentDate.format('MM-DD')}~${momentDate.add(6, 'days').format('MM-DD')}</br>`;
            } else if (timeWindow === IssueTimeType.TIME_TYPE_MONTH) {
              str += `${momentDate.format('YY-MM')}</br>`;
            } else if (timeWindow === IssueTimeType.TIME_TYPE_DAY) {
              str += `${momentDate.format('YYYY-MM-DD')}</br>`;
            } else {
              str += `统计区间：[${momentDate.format('HH:mm')}, ${momentDate.add(isTimeTypeHour ? 1 : 5, isTimeTypeHour ? 'hours' : 'minutes').format('HH:mm')})<br/>`;
            }
          }

          paramsList.forEach((params: any) => {
            const displayValue = getValue(params?.data);
            if (params?.data !== undefined) {
              str += `${params.marker}${params?.seriesName}：${displayValue}`;
            }

            str += '<br />';
          });

          return str;
        },
      },
      xAxis: data.xAxis,
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: data.ySuffix ? `{value} ${data.ySuffix}` : '{value}',
          textStyle: {
            color: '#7B889C',
          },
        },
      },
      series: data.series,
    };
  };

  const formatCombineData = () => {
    const series: any = [];
    const yAxis: any = [];
    const legendData: any = [];
    let newXAxisData;

    props.combineDisplayGroup?.forEach((opt, index) => {
      const chartData = groupData[opt.tabKey] || [];
      const xAxisData = chartData.filter(item => item.time < moment().unix())
        .map(item => formatTimestamp(item.time, DATE_TIME_FORMAT));
      // 获取legend 图例
      newXAxisData = xAxisData;

      const seriesData: number[] = getSeries(
        chartData,
        opt?.unitConfig?.unitFormat?.(state.unit),
      ).slice(0, xAxisData.length);

      const curSeries = {
        id: opt.tabKey,
        xAxisIndex: 0,
        name: opt.name,
        barMaxWidth: '60px',
        smooth: timeWindow !== IssueTimeType.TIME_TYPE_MINUTE5,
        data: seriesData,
        type: opt?.type,
        symbolSize: 7,
        showAllSymbol: false,
        itemStyle: {
          borderWidth: 16,
          borderType: 'solid',
        },
        markPoint: {
          id: 'rect',
          animation: false,
          symbol: 'rect',
          symbolSize: [16, 2560],
          symbolOffset: [0, '-50%'],
          label: {
            show: false,
          },
          itemStyle: {
            color: 'rgba(0, 0, 0, 0)',
          },
          data: [],
        },
        endLabel: {
          position: 'top',
          distance: -18,
          offset: [0, -10],
          show: true,
          formatter: (params: any) => {
            const date = moment(params.name);
            const momentDate = moment();
            if (timeWindow === TimeType.WEEK) {
              const weekDate = moment(params.name).add(6, 'days');
              if (params.dataIndex === seriesData.length - 1 && weekDate.isSameOrAfter(momentDate, 'day')) {
                return '统计中';
              }
            }
            if (timeWindow === TimeType.MONTH) {
              if (params.dataIndex === seriesData.length - 1 && date.isSameOrAfter(momentDate, 'month')) {
                return '统计中';
              }
            }
            if (params.dataIndex === seriesData.length - 1 && momentDate.isSame(date, 'day')) {
              if (timeWindow === TimeType.DAY) {
                return '统计中';
              }
              if (timeWindow === TimeType.HOUR && momentDate.isSame(date, 'hour')) {
                return '统计中';
              }
              if (timeWindow === TimeType.MINUTE5) {
                let minutes: string | string[] = momentDate.format('mm').split('');
                minutes = `${minutes[0]}${Number(minutes?.[1]) >= 5 ? '5' : '0'}`;
                const startsTime = moment(momentDate.format(`YYYY-MM-DD HH:${minutes}:00`));
                if (date.isSame(startsTime)) {
                  return '统计中';
                }
                return '';
              }
            }

            return '';
          },
        },
        yAxisIndex: index,
      };

      const curYAxis = opt.unitConfig && ('options' in opt.unitConfig) ? {
        type: 'value',
        name: opt.name,
        splitNumber: DEFUALT_SPLIT_NUMBER,
        alignTicks: true,
        axisLabel: {
          formatter: `{value}${state.unit}`,
          textStyle: {
            color: '#7B889C',
          },
        },
        position: 'right',
      } : {
        type: 'value',
        name: opt.name,
        splitNumber: DEFUALT_SPLIT_NUMBER,
        position: 'left',
      };

      series.push(curSeries);
      yAxis.push(curYAxis);
      legendData.push(opt.name);
    });

    const xAxis = [{
      type: 'category',
      axisTick: false,
      splitLine: {
        show: false,
      },
      axisLabel: {
        interval: 'auto',
        textStyle: {
          color: '#7B889C',
        },
        formatter(value: moment.MomentInput) {
          switch (timeWindow) {
            case IssueTimeType.TIME_TYPE_DAY:
              return moment(value).format('MM-DD');
            case IssueTimeType.TIME_TYPE_HOUR:
              return moment(value).format('HH');
            case TimeType.WEEK:
              return `${moment(value).format('MM-DD')}~${moment(value).add(6, 'days')
                .format('MM-DD')}`;
            case TimeType.MONTH:
              return moment(value).format('YY-MM');
            default:
              return moment(value).format('HH:mm');
          }
        },
      },
      data: newXAxisData,
      axisLine: {
        lineStyle: {
          color: '#DCE3EE',
        },
      },
    },
    ];


    return {
      xAxis,
      yAxis,
      series,
      legendData,
    };
  };

  const getCombineChartOptions = (): EChartsOption => {
    const data = formatCombineData();
    return  {
      animationDuration: 100,
      legend: {
        bottom: 0,
        data: data.legendData,
      },
      grid: {
        left: 10,
        top: 50,
        bottom: 40,
        right: 30,
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: {
          show: true,
          type: 'line',
          lineStyle: {
            width: 16,
            type: 'solid',
            color: 'rgba(194, 218, 255, 0.5)',
            shadowStyle: {
              color: 'rgba(194, 218, 255, 0.5)',
            },
          },
        },
        formatter(paramsList: any) {
          let str = '';
          const staticParam = paramsList?.[0];
          if (staticParam?.data !== undefined) {
            const date = staticParam?.name;
            const isTimeTypeHour = IssueTimeType.TIME_TYPE_HOUR === timeWindow;
            const momentDate = moment(date, DATE_TIME_FORMAT);
            if (timeWindow === IssueTimeType.TIME_TYPE_WEEK) {
              str += `${momentDate.format('MM-DD')}~${momentDate.add(6, 'days').format('MM-DD')}</br>`;
            } else if (timeWindow === IssueTimeType.TIME_TYPE_MONTH) {
              str += `${momentDate.format('YY-MM')}</br>`;
            } else if (timeWindow === IssueTimeType.TIME_TYPE_DAY) {
              str += `${momentDate.format('YYYY-MM-DD')}</br>`;
            } else {
              str += `统计区间：[${momentDate.format('HH:mm')}, ${momentDate.add(isTimeTypeHour ? 1 : 5, isTimeTypeHour ? 'hours' : 'minutes').format('HH:mm')})<br/>`;
            }
          }

          paramsList.forEach((params: any) => {
            let displayValue = getValue(params?.data);
            if (KEY_NEED_PERCENT.includes(Number(params.seriesId))) {
              displayValue = `${displayValue}${state.unit}`;
            }
            if (params?.data !== undefined) {
              str += `${params.marker}${params?.seriesName}：${displayValue}`;
            }

            str += '<br />';
          });

          return str;
        },
      },
      xAxis: data.xAxis,
      yAxis: data.yAxis,
      series: data.series,
    };
  };

  const formatData = () => {
    const xAxisData = chartData.filter(item => item.time < moment().unix())
      .map(item => formatTimestamp(item.time, DATE_TIME_FORMAT));
    const compareXAxisData = compareChartData?.filter?.(item => item.time < moment().unix())
      .map(item => formatTimestamp(item.time, DATE_TIME_FORMAT)) || [];
    // 获取legend 图例
    let newXAxisData = xAxisData;
    let newCompareXAxisData = compareXAxisData;

    const ySuffix = unitLabel;
    const seriesData: number[] = getSeries(chartData).slice(0, xAxisData.length);
    const compareSeriesData: number[] = getSeries(compareChartData || []).slice(0, compareXAxisData.length);


    // 相同聚合 但日期不相同则补充数据
    if (compareChartData
      && timeWindow === compareTimeWindow && xAxisData?.length && compareXAxisData?.length
      && (xAxisData?.[0] !== compareXAxisData?.[0]
        || xAxisData?.[xAxisData?.length - 1] !== compareXAxisData?.[compareXAxisData?.length - 1])
    ) {
      // 时间范围小于等于1天，则使用时间范围最大的作为X轴
      if ((timeWindow === IssueTimeType.TIME_TYPE_MINUTE5 || timeWindow === IssueTimeType.TIME_TYPE_HOUR)
        && ((moment(xAxisData?.[xAxisData?.length - 1]).diff(moment(xAxisData?.[0]), 'day') < 1) && (moment(compareXAxisData?.[compareXAxisData?.length - 1]).diff(moment(compareXAxisData?.[0]), 'day') < 1))
      ) {
        newXAxisData = xAxisData.length > compareXAxisData.length ? xAxisData : compareXAxisData;
        newCompareXAxisData = newXAxisData;
        const seriesDataUnshiftIndex = newXAxisData.findIndex(value => moment(value).format(HH_MM_SS)
          === moment(xAxisData[0]).format(HH_MM_SS));
        const seriesDataPushIndex = (newXAxisData.length
          - newXAxisData.findIndex(value => moment(value).format(HH_MM_SS)
            === moment(xAxisData[xAxisData.length - 1]).format(HH_MM_SS))) - 1;

        seriesData.unshift(...Array(seriesDataUnshiftIndex >= 0 ? seriesDataUnshiftIndex : 0).fill(undefined));
        seriesData.push(...Array(seriesDataPushIndex >= 0 ? seriesDataPushIndex : 0).fill(undefined));
        const compareSeriesDataUnshiftIndex = newCompareXAxisData.findIndex(value => moment(value).format(HH_MM_SS)
          === moment(compareXAxisData[0]).format(HH_MM_SS));
        const compareSeriesDataPushIndex = (newCompareXAxisData.length
          - compareXAxisData.findIndex(value => moment(value).format(HH_MM_SS)
            === moment(compareXAxisData[compareXAxisData.length - 1]).format(HH_MM_SS)) - 1);
        compareSeriesData.unshift(...Array(compareSeriesDataUnshiftIndex >= 0
          ? compareSeriesDataUnshiftIndex : 0).fill(undefined));
        compareSeriesData.push(...Array(compareSeriesDataPushIndex >= 0
          ? compareSeriesDataPushIndex : 0).fill(undefined));
      } else {
        newXAxisData = Array.from(new Set(xAxisData.concat(compareXAxisData)))
          .sort((a, b) => moment(a).unix() - moment(b).unix());
        newCompareXAxisData = newXAxisData;
        const seriesDataUnshiftIndex = newXAxisData.findIndex(value => value === xAxisData[0]);
        const seriesDataPushIndex = (newXAxisData.length
          - newXAxisData.findIndex(value => value === xAxisData[xAxisData.length - 1])) - 1;

        seriesData.unshift(...Array(seriesDataUnshiftIndex >= 0 ? seriesDataUnshiftIndex : 0).fill(undefined));
        seriesData.push(...Array(seriesDataPushIndex >= 0 ? seriesDataPushIndex : 0).fill(undefined));
        const compareSeriesDataUnshiftIndex = newCompareXAxisData.findIndex(value => value
          === compareXAxisData[0]);
        const compareSeriesDataPushIndex = (newCompareXAxisData.length
          - compareXAxisData.findIndex(value => value
            === compareXAxisData[compareXAxisData.length - 1]) - 1);
        compareSeriesData.unshift(...Array(compareSeriesDataUnshiftIndex >= 0
          ? compareSeriesDataUnshiftIndex : 0).fill(undefined));
        compareSeriesData.push(...Array(compareSeriesDataPushIndex >= 0
          ? compareSeriesDataPushIndex : 0).fill(undefined));
      }
    }


    const series = [{
      id: 'lineA',
      xAxisIndex: 0,
      name: '条件A',
      smooth: timeWindow !== IssueTimeType.TIME_TYPE_MINUTE5,
      data: seriesData,
      type: 'line',
      symbolSize: 7,
      showAllSymbol: false,
      itemStyle: {
        borderWidth: 16,
        borderType: 'solid',
      },
      markPoint: {
        id: 'rect',
        animation: false,
        symbol: 'rect',
        symbolSize: [16, 2560],
        symbolOffset: [0, '-50%'],
        label: {
          show: false,
        },
        itemStyle: {
          color: 'rgba(0, 0, 0, 0)',
        },
        data: [],
      },
      endLabel: {
        position: 'top',
        distance: -18,
        offset: [0, -10],
        show: true,
        formatter: (params: any) => {
          const date = moment(params.name);
          const momentDate = moment();
          if (timeWindow === TimeType.WEEK) {
            const weekDate = moment(params.name).add(6, 'days');
            if (params.dataIndex === seriesData.length - 1 && weekDate.isSameOrAfter(momentDate, 'day')) {
              return '统计中';
            }
          }
          if (timeWindow === TimeType.MONTH) {
            if (params.dataIndex === seriesData.length - 1 && date.isSameOrAfter(momentDate, 'month')) {
              return '统计中';
            }
          }
          if (params.dataIndex === seriesData.length - 1 && momentDate.isSame(date, 'day')) {
            if (timeWindow === TimeType.DAY) {
              return '统计中';
            }
            if (timeWindow === TimeType.HOUR && momentDate.isSame(date, 'hour')) {
              return '统计中';
            }
            if (timeWindow === TimeType.MINUTE5) {
              let minutes: string | string[] = momentDate.format('mm').split('');
              minutes = `${minutes[0]}${Number(minutes?.[1]) >= 5 ? '5' : '0'}`;
              const startsTime = moment(momentDate.format(`YYYY-MM-DD HH:${minutes}:00`));
              if (date.isSame(startsTime)) {
                return '统计中';
              }
              return '';
            }
          }

          return '';
        },
      },
    }];
    const xAxis = [{
      type: 'category',
      axisTick: false,
      splitLine: {
        show: false,
      },
      axisLabel: {
        interval: 'auto',
        textStyle: {
          color: '#7B889C',
        },
        formatter(value: moment.MomentInput) {
          switch (timeWindow) {
            case IssueTimeType.TIME_TYPE_DAY:
              return moment(value).format('MM-DD');
            case IssueTimeType.TIME_TYPE_HOUR:
              return moment(value).format('HH');
            case TimeType.WEEK:
              return `${moment(value).format('MM-DD')}~${moment(value).add(6, 'days')
                .format('MM-DD')}`;
            case TimeType.MONTH:
              return moment(value).format('YY-MM');
            default:
              return moment(value).format('HH:mm');
          }
        },
      },
      data: newXAxisData,
      axisLine: {
        lineStyle: {
          color: '#DCE3EE',
        },
      },
    },
    ];

    if (compareChartData) {
      xAxis.push({
        ...xAxis[0],
        axisLabel: {
          interval: 'auto',
          textStyle: {
            color: '#7B889C',
          },
          formatter(value: moment.MomentInput) {
            switch (compareTimeWindow) {
              case IssueTimeType.TIME_TYPE_DAY:
                return moment(value).format('MM-DD');
              case IssueTimeType.TIME_TYPE_HOUR:
                return moment(value).format('HH');
              case TimeType.WEEK:
                return `${moment(value).format('MM-DD')}~${moment(value).add(6, 'days')
                  .format('MM-DD')}`;
              case TimeType.MONTH:
                return moment(value).format('YY-MM');
              default:
                return moment(value).format('HH:mm');
            }
          },
        },
        data: newCompareXAxisData,
      });
      series.push({
        ...series[0],
        id: 'lineB',
        xAxisIndex: 1,
        name: '条件B',
        smooth: compareTimeWindow !== IssueTimeType.TIME_TYPE_MINUTE5,
        data: compareSeriesData,
        endLabel: {
          position: 'top',
          distance: -18,
          offset: [0, -10],
          show: true,
          formatter: (params: any) => {
            const date = moment(params.name);
            const momentDate = moment();
            if (compareTimeWindow === TimeType.WEEK) {
              const weekDate = moment(params.name).add(6, 'days');
              if (params.dataIndex === compareSeriesData.length - 1 && weekDate.isSameOrAfter(momentDate, 'day')) {
                return '统计中';
              }
            }
            if (compareTimeWindow === TimeType.MONTH) {
              if (params.dataIndex === compareSeriesData.length - 1 && date.isSameOrAfter(momentDate, 'month')) {
                return '统计中';
              }
            }
            if (params.dataIndex === compareSeriesData.length - 1 && momentDate.isSame(date, 'day')) {
              if (compareTimeWindow === TimeType.DAY) {
                return '统计中';
              }
              if (compareTimeWindow === TimeType.HOUR && momentDate.isSame(date, 'hour')) {
                return '统计中';
              }
              if (compareTimeWindow === TimeType.MINUTE5) {
                let minutes: string | string[] = momentDate.format('mm').split('');
                minutes = `${minutes[0]}${Number(minutes?.[1]) >= 5 ? '5' : '0'}`;
                const startsTime = moment(momentDate.format(`YYYY-MM-DD HH:${minutes}:00`));
                if (date.isSame(startsTime)) {
                  return '统计中';
                }
                return '';
              }
            }

            return '';
          },
        },
      });
    }

    return {
      xAxis,
      ySuffix,
      series,
    };
  };

  const getValue = (value: number) => (unitLabel
    ? `${value}${unitLabel}` : formatNum(value || 0));

  const getChartOptions = (): EChartsOption => {
    const data = formatData();
    return  {
      color: ['#0A6CFF', '#52B7FF'],
      animationDuration: 100,
      legend: {
        show: !!compareChartData,
        bottom: 0,
        data: ['条件A', '条件B'],
      },
      grid: {
        left: 10,
        top: 20,
        bottom: 40,
        right: 30,
        containLabel: true,
      },
      tooltip: {
        trigger: 'axis',
        confine: true,
        axisPointer: {
          show: true,
          type: 'line',
          lineStyle: {
            width: 16,
            type: 'solid',
            color: 'rgba(194, 218, 255, 0.5)',
            shadowStyle: {
              color: 'rgba(194, 218, 255, 0.5)',
            },
          },
        },
        formatter(params: any) {
          let str = '';
          const date = params[0]?.name;
          const isTimeTypeHour = IssueTimeType.TIME_TYPE_HOUR === timeWindow;
          const momentDate = moment(date, DATE_TIME_FORMAT);
          const displayValue = getValue(params[0]?.data);
          if (params[0]?.data !== undefined) {
            if (timeWindow === TimeType.WEEK) {
              str = `${momentDate.format('MM-DD')}~${momentDate.add(6, 'days')
                .format('MM-DD')}</br>
          ${params[0].marker}${tabName}：${displayValue}`;
            } else if (timeWindow === TimeType.MONTH) {
              str = `${momentDate.format('YY-MM')}</br>
          ${params[0].marker}${tabName}：${displayValue}`;
            } else {
              str = `${IssueTimeType.TIME_TYPE_DAY === timeWindow ? momentDate.format('YYYY-MM-DD') : `统计区间：[${momentDate.format('HH:mm')}, ${momentDate.add(isTimeTypeHour ? 1 : 5, isTimeTypeHour ? 'hours' : 'minutes').format('HH:mm')})`}</br>
          ${params[0].marker}${tabName}：${displayValue}`;
            }
          }

          if (params[1]?.data !== undefined) {
            if (str) {
              str += '</br></br>';
            }
            const date = params[1]?.name;
            const isTimeTypeHour = IssueTimeType.TIME_TYPE_HOUR === compareTimeWindow;
            const momentDate = moment(date, DATE_TIME_FORMAT);

            const displayValue = getValue(params[1]?.data);

            if (compareTimeWindow === TimeType.WEEK) {
              str +=  `${momentDate.format('MM-DD')}~${momentDate.add(6, 'days')
                .format('MM-DD')}</br>
          ${params[1].marker}${tabName}：${displayValue}`;
            } else if (compareTimeWindow === TimeType.MONTH) {
              str +=  `${momentDate.format('YY-MM')}</br>
          ${params[1].marker}${tabName}：${displayValue}`;
            } else {
              str += `${IssueTimeType.TIME_TYPE_DAY === compareTimeWindow ? momentDate.format('YYYY-MM-DD') : `统计区间：[${momentDate.format('HH:mm')}, ${momentDate.add(isTimeTypeHour ? 1 : 5, isTimeTypeHour ? 'hours' : 'minutes').format('HH:mm')})`}</br>
          ${params[1].marker}${tabName}：${displayValue}`;
            }
          }
          return str;
        },
      },

      xAxis: data.xAxis,
      yAxis: {
        type: 'value',
        axisLabel: {
          formatter: data.ySuffix ? `{value} ${data.ySuffix}` : '{value}',
          textStyle: {
            color: '#7B889C',
          },
        },
      },
      series: data.series,
    };
  };


  if (!loading && ((chartData?.length || 0) === 0) && isEmpty(groupData)) {
    return <Empty style={{ width: '100%',
      height: 262,
      margin: '0 0 20px 0',
      border: '1px solid rgba(5, 5, 5, 0.06)',
      display: 'flex',
      justifyContent: 'center',
      flexDirection: 'column',
    }}/>;
  }
  return <div>
    <ReactECharts ref={(e: any) => {
      chartRef = e?.getEchartsInstance();
    }} showLoading={loading}  echarts={echarts}  option={{}} style={{ width: '100%', height: 262, marginBottom: 20 }} />
  </div>;
};

